import { STORAGE } from '@webready/shared/config';
import { useCallback, useEffect, useState } from 'react';
import { onLCP } from 'web-vitals';

const SLOW_CONNECTION_TYPES = new Set(['slow-2g', '2g', '3g']);
const LCP_FALLBACK_MS = 4000;
const IDLE_TIMEOUT_MS = 2500;
const DEFER_READY_STORAGE_KEY = STORAGE.keys.heroVideoDefer;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function hasSlowOrSaveDataConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return conn.effectiveType != null && SLOW_CONNECTION_TYPES.has(conn.effectiveType);
}

function hasDeferReadyFromPriorVisit(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(DEFER_READY_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markDeferReady(): void {
  try {
    sessionStorage.setItem(DEFER_READY_STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
}

function shouldOpenDeferGateImmediately(): boolean {
  if (typeof document === 'undefined') return false;
  return document.readyState === 'complete' || hasDeferReadyFromPriorVisit();
}

function scheduleAfterLcpOrIdle(onReady: () => void): () => void {
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    markDeferReady();
    onReady();
  };

  try {
    onLCP(() => settle(), { reportAllChanges: false });
  } catch {
    // web-vitals unavailable
  }

  const fallbackTimer = window.setTimeout(settle, LCP_FALLBACK_MS);

  let idleId: number | undefined;
  if ('requestIdleCallback' in window) {
    idleId = (
      window as Window & {
        requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback(settle, { timeout: IDLE_TIMEOUT_MS });
  } else {
    window.setTimeout(settle, IDLE_TIMEOUT_MS);
  }

  return () => {
    window.clearTimeout(fallbackTimer);
    if (idleId != null && 'cancelIdleCallback' in window) {
      (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(
        idleId
      );
    }
  };
}

export interface UseDeferredHeroVideoResult {
  posterOnly: boolean;
  shouldLoadVideo: boolean;
  videoVisible: boolean;
  onVideoCanPlay: () => void;
  resetVideoVisible: () => void;
}

function isMobileScreen(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

export function useDeferredHeroVideo(): UseDeferredHeroVideoResult {
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  const [saveDataOrSlow, setSaveDataOrSlow] = useState(hasSlowOrSaveDataConnection);
  const [isMobile, setIsMobile] = useState(isMobileScreen);
  const [slowLoad, setSlowLoad] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.performance && window.performance.now() > 2000;
  });
  const [deferGateOpen, setDeferGateOpen] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);

  const posterOnly = reduceMotion || saveDataOrSlow || isMobile || slowLoad;
  const shouldLoadVideo = !posterOnly && deferGateOpen;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => setReduceMotion(mq.matches);
    syncMotion();
    mq.addEventListener('change', syncMotion);

    const conn = (navigator as Navigator & {
      connection?: { addEventListener?: (t: string, fn: () => void) => void; removeEventListener?: (t: string, fn: () => void) => void };
    }).connection;
    const syncConnection = () => setSaveDataOrSlow(hasSlowOrSaveDataConnection());
    conn?.addEventListener?.('change', syncConnection);

    const mobileMq = window.matchMedia('(max-width: 767px)');
    const syncMobile = () => setIsMobile(mobileMq.matches);
    syncMobile();
    mobileMq.addEventListener('change', syncMobile);

    return () => {
      mq.removeEventListener('change', syncMotion);
      conn?.removeEventListener?.('change', syncConnection);
      mobileMq.removeEventListener('change', syncMobile);
    };
  }, []);

  useEffect(() => {
    if (posterOnly) {
      setDeferGateOpen(false);
      setVideoVisible(false);
      return;
    }

    if (shouldOpenDeferGateImmediately()) {
      if (typeof window !== 'undefined' && window.performance && window.performance.now() > 2000) {
        setSlowLoad(true);
        setDeferGateOpen(false);
        return;
      }
      markDeferReady();
      setDeferGateOpen(true);
      return;
    }

    return scheduleAfterLcpOrIdle(() => {
      if (typeof window !== 'undefined' && window.performance && window.performance.now() > 2000) {
        setSlowLoad(true);
        setDeferGateOpen(false);
        return;
      }
      setDeferGateOpen(true);
    });
  }, [posterOnly]);

  useEffect(() => {
    if (!shouldLoadVideo) {
      setVideoVisible(false);
    }
  }, [shouldLoadVideo]);

  const onVideoCanPlay = useCallback(() => {
    setVideoVisible(true);
  }, []);

  const resetVideoVisible = useCallback(() => {
    setVideoVisible(false);
  }, []);

  return {
    posterOnly,
    shouldLoadVideo,
    videoVisible,
    onVideoCanPlay,
    resetVideoVisible,
  };
}
